import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { FiSearch } from "react-icons/fi";

import "./LayoutListPage.scss";

import SideNavbar from "../../components/Navbar/SideNavbar";
import Header from "../../components/Header/Header";
import Pagination from "../../components/Pagination/Pagination";
import LayoutList from "../../components/LayoutList/LayoutList";
import { getLayouts } from "../../../core/actionCreators/lmsActionCreator";
import { LAYOUT_PAGE_HEADERS, PAGE_SIZE, PLATFORMS } from "../../../utils/constants";
import AddPagePopup from "../../components/Popup/AddPagePopup";

const SEARCH_DEBOUNCE = 400

const LayoutListPage = () => {
  const dispatch = useDispatch();
  let { layouts, isLoading } = useSelector((s: any) => s.lmsStore);
  const [addPagePopup, setAddPagePopup] = useState(false)

  //Filters, all of them apply together
  const [searchString, setSearchString] = useState("")
  const [createdBy, setCreatedBy] = useState("")
  const [status, setStatus] = useState("")
  const [platform, setPlatform] = useState("")
  const [pageNumber, setPageNumber] = useState(0)

  //Typed filters are debounced into these, dropdowns apply straight away
  const [searchTerm, setSearchTerm] = useState("")
  const [createdByTerm, setCreatedByTerm] = useState("")
  const isFirstRender = useRef(true)

  const fetchLayouts = (_param: string, _callback: any) => {
    dispatch(getLayouts(_param, _callback));
  }

  const buildParam = () => {
    let params = [`pageNumber=${pageNumber}`, `pageSize=${PAGE_SIZE}`]

    if (searchTerm.trim()) params.push(`searchString=${encodeURIComponent(searchTerm.trim())}`)
    if (createdByTerm.trim()) params.push(`createdby=${encodeURIComponent(createdByTerm.trim())}`)
    if (status) params.push(`status=${status}`)
    //Exact, case insensitive match on the layout device
    if (platform) params.push(`device=${encodeURIComponent(platform)}`)

    return params.join("&")
  }

  //Debounce the typed filters so a request does not fire on every keystroke
  useEffect(() => {
    if (isFirstRender.current) return
    const timer = setTimeout(() => {
      setSearchTerm(searchString)
      setCreatedByTerm(createdBy)
      setPageNumber(0)
    }, SEARCH_DEBOUNCE)
    return () => clearTimeout(timer)
  }, [searchString, createdBy])

  //Single fetch for every filter combination
  useEffect(() => {
    isFirstRender.current = false
    fetchLayouts(buildParam(), null)
  }, [searchTerm, createdByTerm, status, platform, pageNumber])

  const searchNowHandler = () => {
    setSearchTerm(searchString)
    setCreatedByTerm(createdBy)
    setPageNumber(0)
  }

  const filterChangeHandler = (_setter: any, _value: string) => {
    _setter(_value)
    setPageNumber(0)
  }

  const isFilterApplied = !!(searchString || createdBy || status || platform)

  const resetFiltersHandler = () => {
    setSearchString("")
    setSearchTerm("")
    setCreatedBy("")
    setCreatedByTerm("")
    setStatus("")
    setPlatform("")
    setPageNumber(0)
  }

  return (
    <>
      {addPagePopup && <AddPagePopup
        addPagePopup={addPagePopup}
        setAddPagePopup={setAddPagePopup}
        onCreated={() => fetchLayouts(buildParam(), null)} />}

      <div className="flex mainWrapper w-full">
        <div className="sidenav-wrapper">
          <SideNavbar />
        </div>
        <div className="right-container">
          <div className="header-container">
            <Header />
          </div>
          <div className="body-container bg-white" >
            <div className="Subheader bg-white p-[15px] shadow-[0_2px_6px_rgba(0,0,0,.1)] flex items-center justify-between">
              <div className="flex items-baseline">
                <p className="text-xl font-bold text-[#43425D]">CMS Layout</p>
                {layouts?.totalCount ?
                  <span className="ml-[10px] text-xs text-[#959595]">{layouts.totalCount} pages</span>
                  : null}
              </div>
              <button onClick={() => setAddPagePopup(true)} className='py-2 px-4 bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75'>Create Page</button>
            </div>
            <div className="pagelist-filter items-end flex flex-wrap p-[15px]">
              <div className="form-field">
                <label>Search</label>
                <div className="input-field">
                  <input
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchNowHandler()}
                    type="text"
                    placeholder="Search by Code, Layout Name"
                  />
                  <div onClick={searchNowHandler} className="search-icon flex justify-around items-center cursor-pointer">
                    <FiSearch size={16} />
                  </div>
                </div>
              </div>
              <div className="form-field">
                <label>Created By</label>
                <input
                  value={createdBy}
                  onChange={(e) => setCreatedBy(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchNowHandler()}
                  type="text"
                  placeholder="Enter Email ID" />
              </div>
              <div className="form-field">
                <label>Status</label>
                <select value={status} onChange={(e) => filterChangeHandler(setStatus, e.target.value)}>
                  <option value="">All Status</option>
                  <option value="true" >Active</option>
                  <option value="false" >Inactive</option>
                </select>
              </div>
              <div className="form-field">
                <label>Platform</label>
                <select value={platform} onChange={(e) => filterChangeHandler(setPlatform, e.target.value)}>
                  <option value="">All Platforms</option>
                  {PLATFORMS.map((p, index) =>
                    <option key={index} value={p.value} >{p.title}</option>
                  )}
                </select>
              </div>
              {isFilterApplied ?
                <button onClick={resetFiltersHandler} className="reset-btn font-semibold text-slate-800 py-2 px-4 rounded">
                  Reset Filters
                </button>
                : null}
            </div>
            <div className="pagelist-tbl">
              <LayoutList isLoading={isLoading}
                headers={LAYOUT_PAGE_HEADERS}
                moduleUsedIn={'layout'}
                emptyMessage={isFilterApplied ? "No pages match these filters." : "No pages found."}
                items={layouts?.data} />
            </div>
            <Pagination
              pageNumber={pageNumber}
              onPageChange={setPageNumber} />
          </div>
        </div>
      </div>
    </>
  );
};

export default LayoutListPage;
